import { memo, useCallback, useEffect, useState } from 'react'

import { fetchMailProfile, updateMailProfile, uploadImageMail } from '@batical/common'
import { batikalColor } from '@batical/design'
import {
  MailProfileState,
  saveUserProfile,
  selectMailProfile,
  selectUserId,
  updateMailProfileAction
} from '@batical/reducer/src'
import { useDispatch, useSelector } from 'react-redux'
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  Container,
  Divider,
  TextField,
  Typography,
  styled
} from '@mui/material'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import Resizer from 'react-image-file-resizer'
import { Link as RouterLink } from 'react-router-dom'

const InputStyled = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(1),
  '& .MuiOutlinedInput-root': {
    borderRadius: 12,
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(0, 0, 0, 0.08)',
    transition: 'all 0.3s ease',
    minHeight: '48px',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      border: '1px solid rgba(0, 0, 0, 0.12)',
      transform: 'translateY(-1px)',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
    },
    '&.Mui-focused': {
      backgroundColor: 'rgba(255, 255, 255, 1)',
      border: `2px solid ${batikalColor}`,
      boxShadow: `0 0 0 4px ${batikalColor}20`,
      minHeight: '48px'
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      border: 'none'
    },
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none'
    },
    '& input': {
      lineHeight: '1.5',
      padding: '12px 14px'
    }
  },
  '& .MuiInputLabel-root': {
    color: '#64748b',
    fontWeight: 500,
    '&.Mui-focused': {
      color: batikalColor
    }
  },
  '& .MuiInputLabel-shrink': {
    transform: 'translate(14px, -9px) scale(0.75)',
    backgroundColor: 'white',
    padding: '0 4px'
  }
}))

export const MailPdfProfilePage = memo(function MailPdfProfilePage() {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const userId = useSelector(selectUserId)
  const mailProfile: MailProfileState = useSelector(selectMailProfile)
  const { control, handleSubmit, setValue } = useForm<Record<string, string | boolean | number>>()
  const [saveHint, setSaveHint] = useState<'idle' | 'ok' | 'err'>('idle')

  useEffect(() => {
    if (!mailProfile) return
    if (mailProfile.name) setValue('name', mailProfile.name)
    if (mailProfile.surname) setValue('surname', mailProfile.surname)
    if (mailProfile.mobile) setValue('mobile', mailProfile.mobile)
    if (mailProfile.phone) setValue('phone', mailProfile.phone)
    if (mailProfile.email) setValue('email', mailProfile.email)
    if (mailProfile.company) setValue('company', mailProfile.company)
    if (mailProfile.siret) setValue('siret', mailProfile.siret)
    if (mailProfile.adress1) setValue('adress1', mailProfile.adress1)
    if (mailProfile.adress2) setValue('adress2', mailProfile.adress2)
    if (mailProfile.city) setValue('city', mailProfile.city)
    if (mailProfile.postal) setValue('postal', mailProfile.postal)
    if (mailProfile.site) setValue('site', mailProfile.site)
    if (mailProfile.logoUrl) setValue('logoUrl', mailProfile.logoUrl)
    if (mailProfile.professional != null) setValue('professional', mailProfile.professional)
  }, [mailProfile, setValue])

  const save = useCallback(
    async (data: Record<string, string | boolean | number>) => {
      if (!userId) return
      setSaveHint('idle')
      const isPro = mailProfile?.professional === true
      const payload: Record<string, string | boolean | number> = { ...data }
      if (isPro) {
        payload.name = ''
        payload.surname = ''
      } else {
        payload.company = ''
        payload.siret = ''
      }
      try {
        await updateMailProfile(userId, payload)
        const fresh = await fetchMailProfile(userId)
        if (fresh) {
          dispatch(saveUserProfile(fresh))
        }
        setSaveHint('ok')
      } catch {
        setSaveHint('err')
      }
    },
    [userId, dispatch, mailProfile?.professional]
  )

  const onSubmit = useCallback(() => {
    handleSubmit(save)()
  }, [handleSubmit, save])

  const onChangeMode = useCallback(
    (index: number) => {
      if (!userId) return
      void updateMailProfile(userId, { professional: index === 1 })
      dispatch(updateMailProfileAction('professional', index === 1))
    },
    [userId, dispatch]
  )

  const handleLogoChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0]
      if (!file || !userId) return
      Resizer.imageFileResizer(
        file,
        500,
        500,
        'JPEG',
        80,
        0,
        uri => {
          void uploadImageMail(userId, uri as Blob, 'jpeg').then(url => {
            dispatch(updateMailProfileAction('logoUrl', url))
          })
        },
        'blob'
      )
      event.target.value = ''
    },
    [dispatch, userId]
  )

  if (!userId) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography>Chargement du compte…</Typography>
      </Container>
    )
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="body2" sx={{ mb: 2 }}>
        <RouterLink to="/profile">← Mon profil (compte)</RouterLink>
      </Typography>

      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography
          variant="h3"
          sx={{
            fontWeight: 800,
            background: `linear-gradient(135deg, ${batikalColor} 0%, ${batikalColor}dd 100%)`,
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 2
          }}>
          {t('WEB.MAILPROFILE.title')}
        </Typography>
        <Typography variant="h6" sx={{ color: '#64748b', fontWeight: 400, maxWidth: 640, mx: 'auto' }}>
          {t('userProfile.intro')}
        </Typography>
        <Typography variant="body2" sx={{ color: '#94a3b8', mt: 1.5, maxWidth: 560, mx: 'auto' }}>
          Ces informations apparaissent sur les PDF et e-mails générés (logo, carte de visite, coordonnées
          société si profil professionnel).
        </Typography>
      </Box>

      {saveHint === 'ok' && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSaveHint('idle')}>
          Enregistré. Les prochains exports utiliseront ces données.
        </Alert>
      )}
      {saveHint === 'err' && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSaveHint('idle')}>
          Impossible d’enregistrer. Vérifiez la connexion et réessayez.
        </Alert>
      )}

      <Card
        sx={{
          borderRadius: 4,
          overflow: 'hidden',
          background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.1)',
          border: '1px solid rgba(0, 0, 0, 0.08)'
        }}>
        <Box sx={{ p: 4, background: `linear-gradient(135deg, ${batikalColor}08 0%, ${batikalColor}dd08 100%)` }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#1a202c', mb: 3, textAlign: 'center' }}>
            Type de profil
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Box
              sx={{
                display: 'flex',
                borderRadius: 3,
                background: 'rgba(255, 255, 255, 0.8)',
                border: '1px solid rgba(0, 0, 0, 0.1)',
                overflow: 'hidden',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
              }}>
              <Button
                onClick={() => onChangeMode(0)}
                sx={{
                  px: 4,
                  py: 2,
                  borderRadius: 0,
                  background: mailProfile?.professional
                    ? 'transparent'
                    : `linear-gradient(135deg, ${batikalColor} 0%, ${batikalColor}dd 100%)`,
                  color: mailProfile?.professional ? '#64748b' : 'white',
                  fontWeight: 600,
                  fontSize: '1rem',
                  textTransform: 'none',
                  minWidth: 140,
                  '&:hover': {
                    background: mailProfile?.professional
                      ? 'rgba(0, 0, 0, 0.05)'
                      : `linear-gradient(135deg, ${batikalColor}dd 0%, ${batikalColor} 100%)`
                  }
                }}>
                {t('userProfile.personnal')}
              </Button>
              <Button
                onClick={() => onChangeMode(1)}
                sx={{
                  px: 4,
                  py: 2,
                  borderRadius: 0,
                  background: mailProfile?.professional
                    ? `linear-gradient(135deg, ${batikalColor} 0%, ${batikalColor}dd 100%)`
                    : 'transparent',
                  color: mailProfile?.professional ? 'white' : '#64748b',
                  fontWeight: 600,
                  fontSize: '1rem',
                  textTransform: 'none',
                  minWidth: 140,
                  '&:hover': {
                    background: mailProfile?.professional
                      ? `linear-gradient(135deg, ${batikalColor}dd 0%, ${batikalColor} 100%)`
                      : 'rgba(0, 0, 0, 0.05)'
                  }
                }}>
                {t('userProfile.professional')}
              </Button>
            </Box>
          </Box>
        </Box>

        <Box sx={{ p: 4 }}>
          <Typography
            variant="h5"
            sx={{
              mb: 2,
              fontWeight: 700,
              color: '#1a202c',
              display: 'flex',
              alignItems: 'center',
              '&::before': {
                content: '""',
                width: 4,
                height: 24,
                background: batikalColor,
                borderRadius: 2,
                mr: 2
              }
            }}>
            Logo en en-tête du PDF / e-mail
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
            <Avatar
              src={mailProfile?.logoUrl || undefined}
              sx={{
                width: 150,
                height: 150,
                border: `4px solid ${batikalColor}`,
                boxShadow: `0 8px 24px ${batikalColor}40`,
                background: mailProfile?.logoUrl ? 'transparent' : 'rgba(0, 0, 0, 0.1)'
              }}>
              {!mailProfile?.logoUrl && '📷'}
            </Avatar>
            <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.9rem' }}>
              Max. 5 Mo — jpg, gif ou png
            </Typography>
            <Button
              component="label"
              variant="contained"
              sx={{
                background: 'linear-gradient(135deg, #374151 0%, #4b5563 100%)',
                color: 'white',
                fontWeight: 600,
                px: 4,
                py: 1.5,
                borderRadius: 2,
                textTransform: 'none',
                '&:hover': {
                  background: 'linear-gradient(135deg, #4b5563 0%, #6b7280 100%)'
                }
              }}>
              {mailProfile?.logoUrl ? 'Changer le logo' : 'Choisir une image'}
              <input hidden accept="image/*" type="file" onChange={handleLogoChange} />
            </Button>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Typography
            variant="h5"
            sx={{
              mb: 2,
              fontWeight: 700,
              color: '#1a202c',
              display: 'flex',
              alignItems: 'center',
              '&::before': {
                content: '""',
                width: 4,
                height: 24,
                background: batikalColor,
                borderRadius: 2,
                mr: 2
              }
            }}>
            Carte de visite
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {!mailProfile?.professional ? (
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ flex: 1, minWidth: 250 }}>
                  <Controller
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <InputStyled fullWidth label="Nom" placeholder="Nom" onChange={onChange} value={value ?? ''} />
                    )}
                    name="name"
                  />
                </Box>
                <Box sx={{ flex: 1, minWidth: 250 }}>
                  <Controller
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <InputStyled fullWidth label="Prénom" placeholder="Prénom" onChange={onChange} value={value ?? ''} />
                    )}
                    name="surname"
                  />
                </Box>
              </Box>
            ) : (
              <>
                <Controller
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <InputStyled
                      fullWidth
                      label="Nom de la société"
                      placeholder="Entrer le nom de la société"
                      onChange={onChange}
                      value={value ?? ''}
                    />
                  )}
                  name="company"
                />
                <Controller
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <InputStyled fullWidth label="SIRET" placeholder="SIRET" onChange={onChange} value={value ?? ''} />
                  )}
                  name="siret"
                />
              </>
            )}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Box sx={{ flex: 1, minWidth: 250 }}>
                <Controller
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <InputStyled fullWidth label="Téléphone" placeholder="Téléphone" onChange={onChange} value={value ?? ''} />
                  )}
                  name="phone"
                />
              </Box>
              <Box sx={{ flex: 1, minWidth: 250 }}>
                <Controller
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <InputStyled fullWidth label="Mobile" placeholder="Mobile" onChange={onChange} value={value ?? ''} />
                  )}
                  name="mobile"
                />
              </Box>
            </Box>
            <Controller
              control={control}
              render={({ field: { onChange, value } }) => (
                <InputStyled fullWidth label="E-mail" placeholder="E-mail" onChange={onChange} value={value ?? ''} />
              )}
              name="email"
            />
          </Box>

          <Divider sx={{ my: 3 }} />
          <Typography
            variant="h5"
            sx={{
              mb: 2,
              fontWeight: 700,
              color: '#1a202c',
              display: 'flex',
              alignItems: 'center',
              '&::before': {
                content: '""',
                width: 4,
                height: 24,
                background: batikalColor,
                borderRadius: 2,
                mr: 2
              }
            }}>
            Adresse
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Controller
              control={control}
              render={({ field: { onChange, value } }) => (
                <InputStyled fullWidth label="Adresse" placeholder="Adresse" onChange={onChange} value={value ?? ''} />
              )}
              name="adress1"
            />
            <Controller
              control={control}
              render={({ field: { onChange, value } }) => (
                <InputStyled
                  fullWidth
                  label="Complément d'adresse"
                  placeholder="Complément"
                  onChange={onChange}
                  value={value ?? ''}
                />
              )}
              name="adress2"
            />
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Box sx={{ flex: 1, minWidth: 250 }}>
                <Controller
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <InputStyled fullWidth label="Ville" placeholder="Ville" onChange={onChange} value={value ?? ''} />
                  )}
                  name="city"
                />
              </Box>
              <Box sx={{ flex: 1, minWidth: 250 }}>
                <Controller
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <InputStyled fullWidth label="Code postal" placeholder="Code postal" onChange={onChange} value={value ?? ''} />
                  )}
                  name="postal"
                />
              </Box>
            </Box>
            <Controller
              control={control}
              render={({ field: { onChange, value } }) => (
                <InputStyled fullWidth label="Site web" placeholder="https://…" onChange={onChange} value={value ?? ''} />
              )}
              name="site"
            />
          </Box>

          <Box sx={{ mt: 4, textAlign: 'center' }}>
            <Button
              onClick={onSubmit}
              variant="contained"
              size="large"
              sx={{
                px: 6,
                py: 2,
                borderRadius: 3,
                background: `linear-gradient(135deg, ${batikalColor} 0%, ${batikalColor}dd 100%)`,
                boxShadow: `0 8px 24px ${batikalColor}30`,
                fontWeight: 600,
                fontSize: '1.1rem',
                textTransform: 'none'
              }}>
              Enregistrer
            </Button>
          </Box>
        </Box>
      </Card>
    </Container>
  )
})

export default MailPdfProfilePage
